/* eslint-disable */
import React, { useContext, useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Loading } from "../components/Loading";

// SDS CONTEXT
import {
    Authentication,
    ConfigContext,
    LinkedApplicationContext,
    NotificationsContext,
    PrintContext,
    ProjectContext,
    TranslationContext,
    UserContext,
    VersionContext,
    getReferrer,
    isValidReferrer
} from "@swegon/sds-core";

import { getProjectIdFromUrl, loadScript, getAhuBaseUrl } from "../ahu/lib";

import "./Home.css";

const AHU_IMPORTED_MODULE_NAME = "ahuBundle";

window.AHU_IS_LOADED = false;

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

const Home = () => {
    // SDS Contexts
    const { config } = useContext(ConfigContext);
    const auth = useContext(Authentication.AuthContext);
    const { l, locale, setLocale, translations, printLanguages, uiLanguages } = useContext(
        TranslationContext
    );
    const { user, settings, saveUserSettings } = useContext(UserContext);
    const { projectApi, projectId, setProjectId, project, setProject } = useContext(ProjectContext);
    const { printApi } = useContext(PrintContext);
    const notificationsContext = useContext(NotificationsContext);
    const versionContext = useContext(VersionContext);
    const {
        isLinkedApplication,
        goToLinkedApplication,
        addValidator: addLinkedApplicationValidator,
        querystring
    } = useContext(LinkedApplicationContext);
    const history = useHistory();
    // AHU Context
    //    const { id: releaseId, url: releaseUrl } = useContext(ReleaseContext);
    const previousValues = usePrevious({ locale, settings });

    // Local state
    const [ahuModule, setAhuModule] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // TODO: Update AHU 1 code to use context
        localStorage.setItem("auth_access_token", auth.getAccessToken());
    }, [auth]);

    useEffect(() => {
        if (ahuModule && uiLanguages) {
            ahuModule.setAvailableLanguages(uiLanguages);
        }
    }, [ahuModule, uiLanguages]);

    // User settings changed
    useEffect(() => {
        if (ahuModule && previousValues && settings.Data.uiLanguage !== previousValues.locale) {
            window.location.hash = window.location.hash.split(";")[0];
            window.location.reload();
        }
    }, [ahuModule, settings]);

    useEffect(() => {
        if (ahuModule && printLanguages) {
            ahuModule.setPrintLanguages(printLanguages);
        }
    }, [ahuModule, printLanguages]);

    useEffect(() => {
        if (!project) {
            return;
        }
        const urlProjectId = getProjectIdFromUrl(window.location.href);
        if (urlProjectId !== project.ProjectId) {
            const referrer = getReferrer(location);
            const ahuApplicationUrl = isValidReferrer(referrer)
                ? `${config.sds_url}${referrer}/`
                : getAhuBaseUrl(window.location.pathname, config);
            const useIdentDev = (localStorage.getItem("sds_ident") || "").toLowerCase() === "dev";
            if (useIdentDev) {
                window.location = `${ahuApplicationUrl}?ident=dev&projectId=${project.ProjectId}`;
            } else {
                window.location = `${ahuApplicationUrl}?projectId=${project.ProjectId}`;
            }
        }
    }, [project]);

    useEffect(async () => {
        const searchParams = new URLSearchParams(window.location.search);
        const maybeLiteAhuId = searchParams.get("ahudLiteId");

        if (!!maybeLiteAhuId) {
            const loadLiteProject = async () => {
                return await projectApi
                    .createLiteProject(maybeLiteAhuId)
                    .then(async response => {
                        const ahuIdFromResponse = response.ahuId;
                        const projectIdFromResponse = response.projectId;

                        if (!projectIdFromResponse && !ahuIdFromResponse) {
                            return;
                        }

                        sessionStorage.setItem("AHUD_LITE_ID", ahuIdFromResponse);
                        sessionStorage.setItem("AHUD_LITE_PROJECT_ID", projectIdFromResponse);
                        localStorage.setItem("projectId", projectIdFromResponse);
                    })
                    .catch(error => {
                        console.error(error);
                    });
            };

            await loadLiteProject();
        }

        const maybeProjectId = getProjectIdFromUrl(window.location.href);
        setLoading(false);

        if (maybeProjectId) {
            setProjectId(maybeProjectId);
            return;
        }

        const maybeProjectIdFromStorage = localStorage.getItem("projectId");

        if (maybeProjectIdFromStorage) {
            setProjectId(maybeProjectIdFromStorage);
            localStorage.removeItem("projectId");
            return;
        }

        const referrer = config.version;
        const useIdentDev = (localStorage.getItem("sds_ident") || "").toLowerCase() === "dev";

        if (isValidReferrer(referrer)) {
            if (useIdentDev) {
                window.location = config.sds_application + `?ident=dev&referrer=${referrer}`;
            } else {
                window.location = config.sds_application + `?referrer=${referrer}${useIdentDev}`;
            }
            return;
        }
        if (useIdentDev) {
            window.location = config.sds_application + `?ident=dev`;
        } else {
            window.location = config.sds_application;
        }
    }, []);

    // Init AHU
    useEffect(() => {
        // make sure we have all data neded to initiate AHU
        if (
            !projectId ||
            !project ||
            !settings.Data ||
            !user.name ||
            !Object.keys(translations).length
        ) {
            setLoading(true);
            return;
        }

        setLoading(false);

        const element = document.getElementById("ahu");

        let appUrl = config.ahujs;

        // if (releaseId !== "working") {
        //     appUrl += releaseId + "/";
        // }
        const spineBundle = `${appUrl}spine.bundle.js?v=${config.version}`;
        const vendorBundle = `${appUrl}vendor.bundle.js?v=${config.version}`;
        const websocketBundle = `${appUrl}workerinit.bundle.js`;

        window.applicationPathVersion = appUrl;

        const initData = {
            apiUrls: {
                icdServer: config.icdServer,
                icdMutateProjectUrl: config.icdMutateProjectUrl,
                authEndpoint: config.authendpoint,
                authUnauthorized: config.authUnauthorized,
                authRedirect: config.redirect_uri,
                logoutEndpoint: config.post_logout_redirect_uri,
                project: config.project,
                ahu: config.ahu,
                ahu_base: config.ahu_base,
                icdjs: config.icdjs,
                climate: config.climate,
                debugging: config.debugging,
                user: config.user,
                language: config.language,
                application: config.application,
                calculations: config.calculations,
                threeDElements: config.threedelements,
                print: config.print,
                cadPluginApi: config.cadplugin,
                pluginSession: config.pluginsession,
                ahuData: config.ahudata_url,
                ahuDataTenantId: config.ahudata_tenantid,
                confWise: config.confwise,
                queue: config.queue,
                progress: config.progress,
                calculationApi: config.calculation_api,
                featureApi: config.feature_api,
                orderApi: config.order_api
            },
            websocketUrl: config.websocketUrl,
            webUrl: config.ahu_web,
            applicationUrl: appUrl,
            applicationVersion: config.version,
            clientId: config.clientId,
            domElement: element,
            environment: config.environment,
            langEnvironment: config.environment,
            useTelerikDocumentGenerationDefaultICD: false,

            // UGLY UseContext Fix for AHU1 in SDS2
            auth: auth,
            user: user,
            userSettings: { ...settings },
            setUserSettings: saveUserSettings,
            l: l,
            locale: locale,
            projectId: project.ProjectId,
            project: project,
            projectApi: projectApi,
            printApi: printApi,
            printLanguages: printLanguages,
            notificationsContext: notificationsContext,
            versionContext: versionContext,
            uiLanguages: uiLanguages,
            translations: translations,
            // Linked Apllication state
            isLinkedApplication,
            goToLinkedApplication,
            addLinkedApplicationValidator,
            linkedApplicationQuery: querystring
        };

        if (config.environment === "DEV") {
            initData.apiUrls.ahuBackend = config.ahu_backend;
        }

        // TODO: INTERACT WITH SDS TO GET PRINT DOCUMENT
        if (!window.AHU_IS_LOADED) {
            loadScript(vendorBundle, AHU_IMPORTED_MODULE_NAME, element);
            loadScript(spineBundle, AHU_IMPORTED_MODULE_NAME, element).then(ahu => {
                /*
                 * Allow Babel-polyfill to be loaded twice.
                 * Todo: stop ICD from loading polyfill
                 * OR separate AHU/ICD from SDS and have them both load/unload separately.
                 */
                if (global._babelPolyfill) {
                    global._babelPolyfill = false;
                }

                // UGLY hack to prevent multiple instances of AHU
                if (!window.AHU_IS_LOADED) {
                    ahu.default.init(initData);
                    setAhuModule(ahu.default);

                    window.AHU_IS_LOADED = true;
                }

                // TODO: UNIT PREFERENCE?
                // TODO: Extend Settings with Unit preferece
                // TODO : Fix print
                // TODO : Fix Conversion units
            });
        }
    }, [projectApi, project, settings, translations, user.name, printLanguages, uiLanguages]);

    if (loading) {
        return <Loading />;
    } else {
        return <></>;
    }
};
export default Home;
